// Libraries
import * as React from "react";

// Utils
import { UitkEGMaps, UitkEGMapsRef, UitkEGMapsType } from "@egds/react-core/map";
import { EGMapConfiguration, LatLng } from "@eg-maps/core";
import { MapPropertySearchByLatLongDocument, MapPropertySearchByLatLongQueryVariables } from "__generated__/typedefs";
import { ModuleMapProps, ModuleMapQueryComponentProps, PoiForMap } from "./typings";
import {
  ViewportChangeCallback,
  convertToEgMapsMarkers,
  registerViewportChangeListener,
  mapTracking,
  getCenter,
} from "./EGMapsUtils";

import { ClickAction } from "./MapsEnums";
import { DISTANCE } from "../Hotels/utils/constants";
import { EGMapErrorMessage } from "./components/EGMapErrorMessage";
import { EGMapSearchButton } from "./components/EGMapSearchButton";
import MapDisplayCard from "components/flexComponents/Maps/components/MapDisplayCard";
import { MapPlace } from "typings/map/eg-map";
// Types
import { MapsFlexModuleResult } from "src/typings/microserviceModels/maps-flex-module";
import { UitkLayoutPosition } from "@egds/react-core/layout-position";
import { observer } from "mobx-react";
import { runInAction } from "mobx";
import { useBexApi } from "@shared-ui/bex-api-context";
import { useLazyQuery } from "@apollo/client";
import { useLocalization } from "@shared-ui/localization-context";
import { useLogger } from "@shared-ui/logger-context";
import { useClickTracker } from "@shared-ui/clickstream-analytics-context";
// Components
import { withStores } from "stores";

export const ModuleMap = withStores(
  "context",
  "analytics",
  "hotels",
  "flexModuleModelStore",
  "flightsStore"
)(
  observer((props: ModuleMapProps) => {
    const { templateComponent, flexModuleModelStore } = props;
    if (!templateComponent) {
      return null;
    }
    const {
      metadata: { id },
    } = templateComponent;

    const model = flexModuleModelStore.getModel(id) as MapsFlexModuleResult | null;

    if (!model) {
      return null;
    }

    return <ModuleMapQueryComponent {...props} model={model} />;
  })
);

const ModuleMapQueryComponent = observer((props: ModuleMapQueryComponentProps) => {
  const { templateComponent, model, context, hotels: hotelsStore, flightsStore } = props;
  const { formatText } = useLocalization();
  const { context: bexApiContext } = useBexApi();
  const { error: logError } = useLogger();

  const [changedBounds, setChangedBounds] = React.useState(null);
  const [selectedMarker, setSelectedMarker] = React.useState(undefined);
  const mapRef = React.useRef<UitkEGMapsRef>(null);
  const track = useClickTracker();

  const componentName = templateComponent.type || "";
  const location = model.location ?? context.searchContext.location;
  const isMobile = context?.deviceInformation?.mobile;
  const searchDaysOffset = model.searchDaysOffset;
  const isSendToHotelInfoSite = model.clickAction === ClickAction.SEND_TO_HOTEL_INFOSITE;

  const [fetchProperties, { data, loading, error }] = useLazyQuery(MapPropertySearchByLatLongDocument);

  if (data && data.propertySearch && !loading) {
    hotelsStore.setAllHotelsOnMapOnHotelsByLatLongSuccessResponse(
      data.propertySearch,
      location.id.toString(10),
      searchDaysOffset
    );
    runInAction(() => {
      hotelsStore.hasMapErrorMessage = false;
    });
  }

  React.useEffect(() => {
    if (error && !loading) {
      logError("MAP_PROPERTY_SEARCH_BY_LAT_LONG_QUERY_ERROR", `message=${error}`);
      runInAction(() => {
        hotelsStore.hasMapErrorMessage = true;
      });
    }
  }, [error]);

  const closeErrorMessage = () => {
    runInAction(() => {
      hotelsStore.hasMapErrorMessage = false;
    });
  };

  const doSearch = (mapCenter: LatLng) => {
    mapTracking(componentName, track, "fullscreen.map.search");

    if (!mapCenter?.lat || !mapCenter?.lng) return;

    const destination: any = {
      coordinates: {
        latitude: mapCenter.lat,
        longitude: mapCenter.lng,
      },
    };

    // if a search radius is specified in the template then pass that to graphql
    if (model.searchRadiusInMeters && window?.google?.maps) {
      const rad = model.searchRadiusInMeters;
      const destLatLong = new window.google.maps.LatLng(mapCenter.lat, mapCenter.lng);
      const destCircle = new window.google.maps.Circle({ radius: rad, center: destLatLong });
      const bounds: any = destCircle && destCircle.getBounds();

      destination.mapBounds = [
        { latitude: bounds.getSouthWest().lat(), longitude: bounds.getSouthWest().lng() },
        { latitude: bounds.getNorthEast().lat(), longitude: bounds.getNorthEast().lng() },
      ];
    }

    const queryVariables: MapPropertySearchByLatLongQueryVariables = {
      context: bexApiContext,
      destination: destination,
      criteria: {
        primary: {
          destination,
          dateRange: hotelsStore.getSearchDateRange(searchDaysOffset),
          rooms: [],
        },
        secondary: {
          selections: [{ id: "sort", value: DISTANCE }],
        },
      },
    };

    fetchProperties({ variables: queryVariables });
  };

  const mapPanTracking = () => {
    mapTracking(componentName, track, "fullscreen.map.pan");
  };

  const onMapPan: ViewportChangeCallback = (center: any, bounds: any) => {
    setChangedBounds(bounds);
    mapPanTracking();
  };

  const onMapZoomIn = () => {
    mapTracking(componentName, track, "fullscreen.map.zoom.in");
  };

  const onMapZoomOut = () => {
    mapTracking(componentName, track, "fullscreen.map.zoom.out");
  };

  const markerTracking = (marker: MapPlace | PoiForMap) => {
    if (marker.icon?.type === "place") {
      mapTracking(componentName, track, "fullscreen.marker.poi");
    } else if (marker.icon?.type === "airport") {
      mapTracking(componentName, track, "flightsMap.marker.airport");
    } else {
      mapTracking(componentName, track, `fullscreen.marker.${marker.id}`);
    }
  };

  const getMarkers = () => {
    switch (model.allowedMarkerNamespaces) {
      case "flights":
        return flightsStore.flightsOnMap as MapPlace[];
      case "hotels":
        if (hotelsStore.allHotelsOnMapLatLong.length === 0) {
          return hotelsStore.allHotelsOnMap;
        }

        return hotelsStore.allHotelsOnMapLatLong;
      default:
        if (hotelsStore.allHotelsOnMapLatLong.length === 0) {
          return [...hotelsStore.allHotelsOnMap, ...flightsStore.flightsOnMap] as MapPlace[];
        }

        return [...hotelsStore.allHotelsOnMapLatLong, ...flightsStore.flightsOnMap] as MapPlace[];
    }
  };

  if (!hotelsStore.allHotelsOnMap.length && !flightsStore.flightsOnMap.length) {
    return null;
  }

  const onSearch = () => {
    const map = mapRef.current?.map?.();
    doSearch(getCenter(map));
  };

  const markers = React.useMemo(() => convertToEgMapsMarkers(getMarkers(), formatText), [
    hotelsStore.allHotelsOnMap,
    hotelsStore.allHotelsOnMapLatLong,
    flightsStore.flightsOnMap,
  ]);

  const initialViewport = React.useMemo(() => markers.map((f) => ({ lat: f.lat, lng: f.lng })), []);

  const onMarkerSelect = (marker: any) => {
    const selectedFeature: any = getMarkers().find((place) => place.id === marker.id);
    setSelectedMarker(selectedFeature);
    if (selectedFeature) markerTracking(selectedFeature);
  };

  const onMarkerSelectionChanged = React.useCallback(
    (event) => {
      const { action, feature } = event;
      switch (action) {
        case "clearSelection":
          setSelectedMarker(undefined);
          break;
        case "select":
          onMarkerSelect(feature);
          break;
        default:
          break;
      }
    },
    [onMarkerSelect]
  );

  const beforeInit = React.useCallback(
    (map) => {
      registerViewportChangeListener(map, onMapZoomIn, onMapZoomOut, onMapPan);
      map.addSelectionChangeListener(onMarkerSelectionChanged);
    },
    [onMapZoomIn, onMapZoomOut, onMapPan, onMarkerSelectionChanged]
  );

  const onInit = (): void => {
    if (props.onMapMounted) {
      props.onMapMounted();
    }
  };

  const getHref = (place: any) => {
    const href = isSendToHotelInfoSite
      ? place.infoSiteUrl
      : hotelsStore.constructPinnedHSRUrl(`${location.id}`, place.id, searchDaysOffset);
    return href;
  };

  const googleToken = "AIzaSyABOFQNLMiTVN0AG3eWNmspVfVC8gNda68";

  const formatFunction = (formatText: (key: string, ...args: any[]) => string) => (key: string, ...args: any[]) =>
    key === "propertyClusterLabelWithPrice" && args.length > 1
      ? formatText("egmaps.markers.cluster.available.label", args[0], args[1])
      : undefined;

  const egMapConfig: EGMapConfiguration = {
    markers: {
      property: {
        actionOnClick: {
          default: ["select", "highlight"],
        },
        actionOnFeatureSelect: {
          default: ["highlight"],
        },
        actionOnFeatureDeselect: {
          default: ["dehighlight"],
        },
        clustering: {
          default: {
            minPoints: 3,
            labelKey: "propertyClusterLabelWithPrice",
            superclusterSettings: {
              radius: 100,
            },
            excludedQualifiers: ["preferred"],
          },
        },
      },
    },
    style: {
      ".cluster-property-marker > .cluster-label": {
        height: "unset",
        maxHeight: "2.5rem",
      },
    },
    googleMaps: {
      gmapsAddConfig: {
        gestureHandling: props.isDynamic ? "cooperative" : "greedy",
      },
    },
    i18n: {
      formatFunction: formatFunction(formatText),
      messages: {
        propertyClusterLabelWithPrice: "{0} hotels\nfrom {1}",
      },
    },
    locale: context.locale,
  };

  return (
    <>
      {googleToken && (
        <UitkEGMaps
          accessToken={googleToken}
          mapType={UitkEGMapsType.GOOGLE}
          mapConfig={egMapConfig}
          egDistancesEnabled
          innerRef={mapRef}
          features={markers}
          initialViewport={initialViewport}
          beforeInit={beforeInit}
          onInit={onInit}
        >
          {selectedMarker && (
            <MapDisplayCard
              componentName={componentName}
              place={selectedMarker}
              isMobile={isMobile}
              isVisible
              href={getHref(selectedMarker)}
            />
          )}
          <UitkLayoutPosition type="absolute" position={{ top: "one", left: "center", zindex: "layer2" }}>
            <EGMapSearchButton onMapSearch={onSearch} isSearchInProgress={loading} changedBounds={changedBounds} />
          </UitkLayoutPosition>
          {hotelsStore.hasMapErrorMessage && (
            <UitkLayoutPosition
              type="absolute"
              position={{ bottom: "one", left: "one", right: "one", zindex: "layer2" }}
            >
              <EGMapErrorMessage
                heading={formatText("maps.filter.error.areaError.header")}
                message={formatText("maps.filter.error.areaError.message")}
                onDismiss={closeErrorMessage}
              />
            </UitkLayoutPosition>
          )}
        </UitkEGMaps>
      )}
    </>
  );
});

ModuleMap.displayName = "ModuleMap";

export default ModuleMap;
